import { useCallback, useEffect, useState } from 'react';

import {
  getToday,
  getActiveMonths,
  getNextMonths,
  isSelectable,
  isDateControlled,
} from '../helpers';
import { DatePickerAllArgs, Month, UpdateActiveMonths } from '../types';

type UseNavigationArgs = Pick<
  DatePickerAllArgs,
  'value' | 'startDate' | 'endDate' | 'minDate' | 'maxDate' | 'timeZone'
> & {
  range?: boolean;
  numberOfMonths: number;
  onDateSelected?: (
    { selectable, date }: { selectable: boolean; date: Date },
    updateActiveMonths: UpdateActiveMonths,
  ) => void;
  onMonthChange?: (nextActiveMonths: Month[]) => void;
};

/**
 * This hooks is incharge of month/year navigation.
 */
function useNavigation({
  range,
  value,
  startDate,
  endDate,
  numberOfMonths,
  onDateSelected,
  onMonthChange,
  minDate,
  maxDate,
  timeZone,
}: UseNavigationArgs) {
  const [activeMonths, setActiveMonths] = useState(
    getActiveMonths({
      numberOfMonths,
      startDate: range ? startDate : value,
      endDate,
      minDate,
      maxDate,
    }),
  );

  const setActiveMonthsWrapped = useCallback(
    (nextActiveMonths: Month[]) => {
      setActiveMonths(nextActiveMonths);
      onMonthChange?.(nextActiveMonths);
    },
    [setActiveMonths, onMonthChange],
  );

  const updateActiveMonths = useCallback(
    (
      newRange: boolean,
      newDate?: Date | null,
      newStartDate?: Date | null,
      newEndDate?: Date | null,
    ) => {
      if (newRange) {
        setActiveMonthsWrapped(
          getActiveMonths({
            numberOfMonths,
            startDate: newStartDate,
            endDate: newEndDate,
          }),
        );
      } else {
        setActiveMonthsWrapped(
          getActiveMonths({ numberOfMonths, startDate: newDate }),
        );
      }
    },
    [numberOfMonths, setActiveMonthsWrapped],
  );

  const isControlled = isDateControlled({
    range,
    date: value,
    startDate,
    endDate,
  });

  useEffect(() => {
    // When the startDate and endDate are controlled we need to ensure the months are
    // updated too
    if (isControlled && endDate && startDate) {
      updateActiveMonths(!!range, value, startDate, endDate);
    }
  }, [range, isControlled, value, startDate, endDate, updateActiveMonths]);

  // set active month to a specific month
  const setMonth = useCallback(
    (monthIndex: number, monthToSet: number) => {
      const clone = [...activeMonths];
      clone[monthIndex].month = monthToSet;
      clone[monthIndex].date = new Date(clone[monthIndex].year, monthToSet, 1);

      setActiveMonthsWrapped(clone);
    },
    [activeMonths, setActiveMonthsWrapped],
  );

  // set active year to a specific year
  const setYear = useCallback(
    (monthIndex: number, yearToSet: number) => {
      const clone = [...activeMonths];
      clone[monthIndex].year = yearToSet;
      clone[monthIndex].date = new Date(yearToSet, clone[monthIndex].month, 1);

      setActiveMonthsWrapped(clone);
    },
    [activeMonths, setActiveMonthsWrapped],
  );

  // next month(s) used in next arrow
  const nextMonth = useCallback(() => {
    setActiveMonthsWrapped(getNextMonths(activeMonths, numberOfMonths, 1, 1));
  }, [setActiveMonthsWrapped, activeMonths, numberOfMonths]);

  // prev month(s) used in prev arrow
  const prevMonth = useCallback(() => {
    setActiveMonthsWrapped(getNextMonths(activeMonths, numberOfMonths, -1, 1));
  }, [setActiveMonthsWrapped, activeMonths, numberOfMonths]);

  const resetViewToTodayAndSelect = useCallback(() => {
    const newlySelectedDate = getToday(timeZone);
    onDateSelected?.(
      {
        selectable: isSelectable({ minDate, maxDate, date: newlySelectedDate }),
        date: newlySelectedDate,
      },
      updateActiveMonths,
    );
  }, [onDateSelected, minDate, maxDate, updateActiveMonths, timeZone]);

  return {
    activeMonths,
    updateActiveMonths,
    setMonth,
    setYear,
    nextMonth,
    prevMonth,
    resetViewToTodayAndSelect,
  };
}

export default useNavigation;
